import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Tokens automatisch anhängen
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Token-Refresh bei 401
client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh/`, { refresh });
          localStorage.setItem('access_token', data.access);
          original.headers.Authorization = `Bearer ${data.access}`;
          return client(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default client;

// API-Methoden
export const authApi = {
  login: (email, password) => client.post('/auth/login/', { email, password }),
  logout: (refresh) => client.post('/auth/logout/', { refresh }),
  me: () => client.get('/auth/me/'),
  updateMe: (data) => client.patch('/auth/me/', data),
  changePassword: (data) => client.post('/auth/change-password/', data),
};

export const customerApi = {
  list: (params) => client.get('/customers/', { params }),
  get: (id) => client.get(`/customers/${id}/`),
  create: (data) => client.post('/customers/', data),
  update: (id, data) => client.patch(`/customers/${id}/`, data),
  delete: (id) => client.delete(`/customers/${id}/`),
  stats: (id) => client.get(`/customers/${id}/stats/`),
  pause: (id) => client.post(`/customers/${id}/pause/`),
  activate: (id) => client.post(`/customers/${id}/activate/`),
  block: (id) => client.post(`/customers/${id}/block/`),
  dashboardStats: () => client.get('/admin/dashboard-stats/'),
};

export const mailAccountApi = {
  list: (params) => client.get('/mail-accounts/', { params }),
  get: (id) => client.get(`/mail-accounts/${id}/`),
  create: (data) => client.post('/mail-accounts/', data),
  update: (id, data) => client.patch(`/mail-accounts/${id}/`, data),
  delete: (id) => client.delete(`/mail-accounts/${id}/`),
  testConnection: (id) => client.post(`/mail-accounts/${id}/test-connection/`),
  sendTestMail: (id) => client.post(`/mail-accounts/${id}/send-test-mail/`),
  fetchNow: (id) => client.post(`/mail-accounts/${id}/fetch-now/`),
};

export const mailApi = {
  list: (params) => client.get('/mails/', { params }),
  get: (id) => client.get(`/mails/${id}/`),
  markRead: (id) => client.post(`/mails/${id}/mark-read/`),
  reclassify: (id) => client.post(`/mails/${id}/reclassify/`),
  markSpam: (id, isSpam) => client.post(`/mails/${id}/mark-spam/`, { is_spam: isSpam }),
  delete: (id) => client.delete(`/mails/${id}/`),
  bulkDelete: (ids) => client.post('/mails/bulk-delete/', { ids }),
  bulkSpam: (ids, isSpam = true) => client.post('/mails/bulk-spam/', { ids, is_spam: isSpam }),
  resetAllClassifications: () => client.post('/mails/reset-all-classifications/'),
};

export const categoryApi = {
  list: (params) => client.get('/categories/', { params }),
  create: (data) => client.post('/categories/', data),
  update: (id, data) => client.patch(`/categories/${id}/`, data),
  delete: (id) => client.delete(`/categories/${id}/`),
};

export const ruleApi = {
  list: (params) => client.get('/rules/', { params }),
  create: (data) => client.post('/rules/', data),
  update: (id, data) => client.patch(`/rules/${id}/`, data),
  delete: (id) => client.delete(`/rules/${id}/`),
  applyAll: () => client.post('/rules/apply-all/'),
};

export const reportApi = {
  list: (params) => client.get('/reports/', { params }),
  stats: () => client.get('/reports/stats/'),
};

export const myCustomerApi = {
  get: () => client.get('/my-customer/'),
  update: (data) => client.patch('/my-customer/', data),
  largeAttachments: (limit = 50) => client.get('/my-customer/large-attachments/', { params: { limit } }),
};

export const contactApi = {
  // Volle Kontaktliste (Einstellungen-Seite), optional mit Suchbegriff
  list: (params) => client.get('/contacts/', { params }),
  update: (id, data) => client.patch(`/contacts/${id}/`, data),
  delete: (id) => client.delete(`/contacts/${id}/`),
  // Schlanke Top-N-Vorschlagsliste fürs An-Feld beim Verfassen
  autocomplete: (q, limit = 8) => client.get('/my-customer/contacts-autocomplete/', { params: { q, limit } }),
};

export const usageApi = {
  list: (params) => client.get('/api-usage/', { params }),
  monthlySummary: () => client.get('/api-usage/monthly-summary/'),
};

export const logApi = {
  list: (params) => client.get('/audit/logs/', { params }),
};

export const auditLogApi = {
  list: (params) => client.get('/audit/audit-logs/', { params }),
};

export const userApi = {
  list: (params) => client.get('/users/', { params }),
  create: (data) => client.post('/users/', data),
  update: (id, data) => client.patch(`/users/${id}/`, data),
  delete: (id) => client.delete(`/users/${id}/`),
  resetPassword: (id, data) => client.post(`/users/${id}/reset-password/`, data),
};

export const outboundApi = {
  list: (params) => client.get('/outbound/', { params }),
  // Bei FormData (neue Datei-Anhänge) den vorgegebenen JSON-Header entfernen,
  // damit der Browser den multipart/form-data-Boundary selbst setzen kann.
  send: (data) => client.post('/outbound/', data,
    data instanceof FormData ? { headers: { 'Content-Type': undefined } } : undefined),
  resend: (id) => client.post(`/outbound/${id}/resend/`),
};

export const attachmentApi = {
  // Lädt Anhang als Blob herunter (JWT-Token wird automatisch mitgesendet)
  download: (id) => client.get(`/attachments/${id}/download/`, { responseType: 'blob' }),
  delete: (id) => client.delete(`/attachments/${id}/`),
};

export const outboundAttachmentApi = {
  download: (id) => client.get(`/outbound-attachments/${id}/download/`, { responseType: 'blob' }),
  delete: (id) => client.delete(`/outbound-attachments/${id}/`),
};

export const pushApi = {
  getVapidPublicKey: () => client.get('/push/vapid-public-key/'),
  subscribe: (subscription) => client.post('/push/subscribe/', subscription),
  unsubscribe: (endpoint) => client.post('/push/unsubscribe/', { endpoint }),
};
